import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../features/auth/context';
import { safeNextPath } from '../features/auth/nextPath';
import GoogleSignInButton from '../features/auth/GoogleSignInButton';
import Input from '../components/ui/Input';
import FormField from '../components/ui/FormField';
import { useToast } from '../hooks/useToast';
import { useDocumentTitle } from '../shared/useDocumentTitle';

const LoginPage: React.FC = () => {
  useDocumentTitle('Log in');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { login, googleLogin, user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  // Where the visitor was heading before RequireAuth sent them here. Validated
  // against a same-origin allowlist (features/auth/nextPath.ts) — this value
  // comes from the URL, so it is untrusted. Falls back to '/', which
  // RootRedirect turns into the usual /trips landing.
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get('next');
  const destination = safeNextPath(nextParam) ?? '/';

  // Redirect if user is already logged in
  useEffect(() => {
    if (user) {
      navigate(destination, { replace: true });
    }
  }, [user, navigate, destination]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      navigate(destination, { replace: true });
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error || err?.response?.data?.message || '';
      const message = /invalid/i.test(serverMsg)
        ? 'Invalid email or password.'
        : 'Login failed. Please try again.';
      setError(message);
      showToast({
        title: 'Login Error',
        message,
        type: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Signing up instead of logging in must not drop the destination either.
  const signupHref = nextParam && safeNextPath(nextParam)
    ? `/signup?next=${encodeURIComponent(safeNextPath(nextParam) as string)}`
    : '/signup';

  // One "Continue with Google" for both logging in and signing up: the backend
  // logs in a known Google account, auto-links a verified email, or creates
  // the account. People often don't remember whether they already have an
  // account, so a new Google user is never bounced to /signup from here. The
  // consent for a brand-new account is the clickwrap disclosure line under
  // the button, the same line the Signup page shows (Paulo, 2026-10-08).
  const handleGoogleCredential = async (credential: string) => {
    setError(null);
    setLoading(true);
    try {
      await googleLogin({
        credential,
        acceptedTerms: true,
        acceptedPrivacyPolicy: true
      });
      navigate(destination, { replace: true });
    } catch (err: any) {
      const message = 'Google sign-in failed. Please try again.';
      setError(message);
      showToast({
        title: 'Login Error',
        message,
        type: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Bounced here from a trip/journey deep link (RequireAuth/RequireAuthOrGuest
  // preserve it in ?next=). Say WHY the login is being asked, and name the
  // share-link alternative: the common way to land on someone else's trip URL
  // is a forwarded link, and the right door for that is /s/<token>, which
  // needs no account (trip sharing v1 — Paulo 2026-09-11). An inline banner,
  // not a toast: the visitor arrives mid-redirect and needs the context to
  // stay on screen while they decide.
  const deepLinkNotice = /^\/(trips|journeys)\/.+/.test(destination);

  return (
    <div style={{ maxWidth: 400, margin: '2rem auto', padding: '0 1rem' }}>
      <h1>Login</h1>
      {deepLinkNotice && (
        <div
          style={{
            background: '#e8f2ec',
            border: '1px solid #cfe3d8',
            borderRadius: 12,
            padding: '10px 14px',
            marginBottom: '1rem',
            fontSize: 13.5,
            color: '#16241d',
            lineHeight: 1.55,
          }}
        >
          Log in to open this page. It is only visible to the account it
          belongs to. If someone wants to show you their trip, ask them to
          send you their share link: it opens without an account.
        </div>
      )}
      {error && <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}

      <GoogleSignInButton
        onCredential={handleGoogleCredential}
        disclosure={
          <>
            By continuing with Google, you agree to Jody's{' '}
            <a
              href="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--green-primary-bg)', textDecoration: 'underline' }}
            >
              Terms and Conditions
            </a>{' '}
            and{' '}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--green-primary-bg)', textDecoration: 'underline' }}
            >
              Privacy Policy
            </a>
            .
          </>
        }
      />

      <form onSubmit={handleSubmit}>
        <FormField label="Email">
          <Input
            type="email"
            name="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </FormField>
        
        <FormField label="Password">
          <Input
            type="password"
            name="password"
            placeholder="Your secret password"
            value={formData.password}
            onChange={handleChange}
            required
          />
        </FormField>
        
        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '0.75rem',
            backgroundColor: loading ? '#ccc' : 'var(--green-accent-bg)',
            color: 'var(--green-accent-fg)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Logging in...' : 'Login'}
        </button>
      </form>

      <p style={{ textAlign: 'center', marginTop: '1rem' }}>
        Don't have an account? <a href={signupHref}>Get started</a>
      </p>
    </div>
  );
};

export default LoginPage; 